import { makeAutoObservable } from "mobx";
import RootStore from "./RootStore";
import { RouteType } from "types/RouteType";
import { RouterStore } from "./RouterStore";

export interface LayoutOptions {
  toolboxBase: string;
  rightNavButtonTitle: string;
  rightNavDefaultRoute: string;
}

interface RightMenuItem {
  city: string | null | undefined;
  address: string;
  stateLocation: string;
  projectArchived?: boolean;
  archived?: boolean;
  projectTitle?: string;
  jobNumber?: number | string;
  shortName?: string;
  name: string;
}

/**
 * Extended userPermissions interface with plantId field.
 * This extends the base type once backend adds plantId support.
 */
interface UserPermissionWithPlant {
  id: number;
  userId: number;
  appName: string;
  location: string;
  createAllowed: boolean;
  findAllowed: boolean;
  patchAllowed: boolean;
  removeAllowed: boolean;
  isDenied: boolean;
  plantId?: string; // "all" or specific plant ID - added for plant-based filtering
}

// Search result categories for enhanced omnibar
export enum SearchCategory {
  PAGES = "pages",
  PROJECTS = "projects",
  ACTIONS = "actions",
  RECENT = "recent",
  NAVIGATION = "navigation",
}

class AppLayoutStore {
  debugMode = false;
  leftMenuFilter = "";
  rightMenuFilter = "";
  rightNavDefaultRoute = "";
  omnibarSearchQuery = "";
  omnibarIsOpen = false;
  isOmnibarFocused = false;

  // Company selection and color coding
  selectedCompanyId: string | null = null;
  showCompanyColors = false;

  // Enhanced OmniBar observables - Phase 1
  selectedSearchIndex = -1;
  recentSearches: string[] = [];
  trendingSearches: string[] = [];
  searchCategories = new Map<string, any[]>();
  isSearchActive = false;
  searchInputFocused = false;

  omnibarFavorites = [
    {
      title: "Purchase Orders",
      action: () => this.rootStore.routerStore?.goTo("PurchaseOrders"),
      type: "page",
      subtitle: "Page",
    },
    {
      title: "Production Floor Map",
      action: () => this.rootStore.routerStore?.goTo("ProductionFloorMap"),
      type: "page",
      subtitle: "Page",
    },
    {
      title: "Production Monitor",
      action: () => this.rootStore.routerStore?.goTo("ProductionMonitor"),
      type: "page",
      subtitle: "Page",
    },
    {
      title: "Production Schedule Overview",
      action: () =>
        this.rootStore.routerStore?.goTo("ProductionScheduleOverview"),
      type: "page",
      subtitle: "Page",
    },
    {
      title: "AI Agent",
      action: () => this.rootStore.routerStore?.goTo("AIAgent"),
      type: "page",
      subtitle: "Page",
    },
  ];

  omnibarAnchorEl: HTMLElement | null = null; // For popover anchor
  popoverRef: HTMLElement | null = null; // For click-outside detection

  toolboxBase: string = ""; // Add the toolboxBase property
  rightNavButtonTitle: string = ""; // Add the rightNavButtonTitle property

  showArchiveMenuItems = false;
  rootStore: RootStore;
  options: unknown;

  constructor(rootStore: RootStore, layoutOptions: LayoutOptions) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
    // AppLayoutStore initialized with layoutOptions

    if (layoutOptions) {
      this.toolboxBase = layoutOptions.toolboxBase;
      this.rightNavButtonTitle = layoutOptions.rightNavButtonTitle;
      this.rightNavDefaultRoute = layoutOptions.rightNavDefaultRoute;
      this.options = layoutOptions;
    }

    // Initialize enhanced OmniBar features
    this.loadRecentSearches();
    this.loadTrendingSearches();
    this.loadCompanyPreferences();
  }

  setAnchorEl = (el: HTMLElement | null) => {
    this.omnibarAnchorEl = el;
  };

  setPopoverRef = (el: HTMLElement | null) => {
    this.popoverRef = el;
  };

  setLeftMenuFilter = (value: string) => {
    this.leftMenuFilter = value;
  };

  setRightMenuFilter = (value: string) => {
    this.rightMenuFilter = value;
  };

  setShowArchiveMenuItems = () => {
    this.showArchiveMenuItems = !this.showArchiveMenuItems;
  };

  setOmnibarSearchQuery = (value: string) => {
    this.omnibarSearchQuery = value;
    this.omnibarIsOpen = value.length > 0;
  };

  setOmnibarIsOpen = (value: boolean) => {
    this.omnibarIsOpen = value;
    if (!value) {
      this.omnibarSearchQuery = "";
    }
  };

  setOmnibarFocused = (value: boolean) => {
    this.isOmnibarFocused = value;
  };

  clearOmnibarState = () => {
    this.omnibarSearchQuery = "";
    this.omnibarIsOpen = false;
    this.isOmnibarFocused = false;
    this.omnibarAnchorEl = null;
    this.selectedSearchIndex = -1;
    this.isSearchActive = false;
    this.searchInputFocused = false;
  };

  // Enhanced OmniBar actions - Phase 1

  // Keyboard navigation
  setSelectedSearchIndex = (index: number) => {
    this.selectedSearchIndex = index;
  };

  handleArrowNavigation = (direction: "up" | "down") => {
    const maxIndex = this.totalResultCount - 1;

    if (direction === "down") {
      this.selectedSearchIndex = Math.min(
        this.selectedSearchIndex + 1,
        maxIndex
      );
    } else {
      this.selectedSearchIndex = Math.max(this.selectedSearchIndex - 1, -1);
    }
  };

  navigateToSelectedResult = () => {
    const selectedResult = this.selectedResult;
    if (selectedResult) {
      selectedResult.action();
      this.clearOmnibarState();
    }
  };

  // Search history management
  addRecentSearch = (search: string) => {
    if (!search.trim()) return;

    // Remove if already exists and add to front
    this.recentSearches = [
      search,
      ...this.recentSearches.filter((s) => s !== search),
    ].slice(0, 10); // Keep only 10 recent searches

    // Persist to localStorage
    try {
      localStorage.setItem(
        "omnibar-recent-searches",
        JSON.stringify(this.recentSearches)
      );
    } catch (error) {
      console.warn("Failed to save recent searches to localStorage:", error);
    }
  };

  clearRecentSearches = () => {
    this.recentSearches = [];
    try {
      localStorage.removeItem("omnibar-recent-searches");
    } catch (error) {
      console.warn("Failed to clear recent searches from localStorage:", error);
    }
  };

  loadTrendingSearches = async () => {
    // For now, use predefined trending searches
    // In the future, this could fetch from analytics API
    this.trendingSearches = [
      "Production Monitor",
      "Purchase Orders",
      "Quality Control",
      "Project Dashboard",
      "Reports",
    ];
  };

  // Category management
  categorizeSearchResults = (results: any[]) => {
    const categories = new Map<string, any[]>();

    results.forEach((result) => {
      const category = this.determineCategory(result);
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category)!.push(result);
    });

    this.searchCategories = categories;
  };

  private determineCategory = (result: any): string => {
    if (result?.type === "page") return SearchCategory.PAGES;
    if (result?.type === "project") return SearchCategory.PROJECTS;
    if (result?.type === "action") return SearchCategory.ACTIONS;
    if (result?.type === "recent") return SearchCategory.RECENT;
    return SearchCategory.NAVIGATION;
  };

  getResultsByCategory = (category: string) => {
    return this.searchCategories.get(category) || [];
  };

  // Enhanced UI state
  setSearchActive = (active: boolean) => {
    this.isSearchActive = active;
  };

  setSearchInputFocused = (focused: boolean) => {
    this.searchInputFocused = focused;
  };

  // Load recent searches from localStorage on initialization
  loadRecentSearches = () => {
    try {
      const saved = localStorage.getItem("omnibar-recent-searches");
      if (saved) {
        this.recentSearches = JSON.parse(saved);
      }
    } catch (error) {
      console.warn("Failed to load recent searches from localStorage:", error);
      this.recentSearches = [];
    }
  };

  // Company selection and color coding methods
  loadCompanyPreferences = () => {
    try {
      const savedCompanyId = localStorage.getItem("selected-company-id");
      const savedShowColors = localStorage.getItem("show-company-colors");

      if (savedCompanyId) {
        this.selectedCompanyId = savedCompanyId === "null" ? null : savedCompanyId;
      }
      if (savedShowColors) {
        this.showCompanyColors = savedShowColors === "true";
      }
    } catch (error) {
      console.warn("Failed to load company preferences from localStorage:", error);
    }
  };

  setSelectedCompanyId = (companyId: string | null) => {
    this.selectedCompanyId = companyId;
    try {
      localStorage.setItem("selected-company-id", companyId || "null");
    } catch (error) {
      console.warn("Failed to save company selection to localStorage:", error);
    }
  };

  setShowCompanyColors = (show: boolean) => {
    this.showCompanyColors = show;
    try {
      localStorage.setItem("show-company-colors", show.toString());
    } catch (error) {
      console.warn("Failed to save company colors preference to localStorage:", error);
    }
  };

  toggleCompanyColors = () => {
    this.setShowCompanyColors(!this.showCompanyColors);
  };

  get leftMenuRoutes() {
    const { routerStore, authStore } = this.rootStore;
    const isAdminUser = authStore?.isAdminUser;
    const { routes, routesWithPermissions } = routerStore || {};
    if (!routes || Object.keys(routes).length === 0) return [];
    const allRoutesNames = routes ? Object.keys(routes) : [];
    this.debugMode && console.log("allRoutesNames", allRoutesNames);
    const allPageRoutes = allRoutesNames
      .slice()
      .filter((routeName: any) => {
        this.debugMode && console.log("routeName", routeName);
        const routeNameWithoutSpaces = routeName.replace(/ /g, "");
        this.debugMode &&
          console.log("routeNameWithoutSpaces", routeNameWithoutSpaces);
        if (routeNameWithoutSpaces === "Dashboard") return false; // Added at top of list after sort
        if (routeNameWithoutSpaces === "Home") return false;
        if (routeNameWithoutSpaces === "Settings") return false;
        if (routeNameWithoutSpaces === "DatabaseManager") return false;
        const currentRoute = routes
          ? routes[routeNameWithoutSpaces]
          : undefined;
        if (currentRoute) {
          this.debugMode && console.log("currentRoute", currentRoute);
          if (currentRoute.requiresAdmin) {
            this.debugMode &&
              console.log(
                "currentRoute.requiresAdmin",
                currentRoute.requiresAdmin
              );
            if (currentRoute.parentRoute) return false;
            if (currentRoute.projectRoute) return false;
            if (currentRoute.forwardRoute) return false;
            if (currentRoute.hidden) return false;
            this.debugMode && console.log("isAdminUser", isAdminUser);
            return isAdminUser;
          }
          this.debugMode &&
            console.log("after currentRoute.requiresAdmin", routeName);
          if (currentRoute.requiresPermission) {
            const allRoutesWithPermission = (routesWithPermissions ?? []).map(
              (routeObj: any) => routeObj.name?.replace(/ /g, "") || ""
            );
            if (!allRoutesWithPermission.includes(routeNameWithoutSpaces))
              return false;
          }
          this.debugMode &&
            console.log("after currentRoute.requiresPermission", routeName);
          if (currentRoute.projectRoute) return false;
          if (currentRoute.parentRoute) return false;
          if (
            currentRoute.path &&
            currentRoute.path.includes(`/${this.toolboxBase}/`)
          )
            return false;
          if (currentRoute.path && currentRoute.path.includes("/auth/"))
            return false;
          if (currentRoute.forwardRoute) return false;
          if (currentRoute.hidden) return false;
          if (currentRoute.subRoutes && currentRoute.subRoutes.length)
            return false;
          this.debugMode &&
            console.log("Route is a leftMenuRoute", currentRoute);
        } else {
          this.debugMode &&
            console.warn("currentRoute is undefined", routeName);
        }
        return true;
      })
      .map((routeName: any) => {
        const currentRoute = routes ? routes[routeName] : undefined;
        return currentRoute?.title ? currentRoute.title : routeName;
      })
      .sort();
    this.debugMode && console.warn("allPageRoutes", allPageRoutes);
    return ["Home", ...allPageRoutes].map((routeName: any) => {
      return { title: routeName };
    });
  }

  get leftMenuFilteredPages() {
    if (!this.leftMenuRoutes || this.leftMenuRoutes.length === 0) return [];
    return this.leftMenuRoutes.slice().filter((page: any) => {
      if (!this.leftMenuFilter) return true;
      return page.title
        .toLowerCase()
        .includes(this.leftMenuFilter.toLowerCase());
    });
  }

  get toolboxRoutes() {
    const { routerStore, authStore } = this.rootStore;
    const isAdminUser = authStore?.isAdminUser;
    const { routes, routesWithPermissions } = routerStore || {};
    const allRoutesNames = routes ? Object.keys(routes) : [];
    const currentProjectRoutes = allRoutesNames
      .filter((routeName: any) => {
        const currentRoute = routes ? routes[routeName] : undefined;

        if (
          currentRoute &&
          currentRoute.requiresAdmin &&
          currentRoute.projectRoute
        ) {
          if (currentRoute && currentRoute.parentRoute) return false;
          if (currentRoute && currentRoute.forwardRoute) return false;
          if (currentRoute && currentRoute.hidden) return false;
          return isAdminUser;
        }
        if (currentRoute && currentRoute.requiresPermission) {
          const allRoutesWithPermission = (routesWithPermissions ?? []).map(
            (routeObj: any) => routeObj.name?.replace(/ /g, "") || ""
          );
          if (!allRoutesWithPermission.includes(routeName)) return false;
        }
        if (currentRoute && currentRoute.parentRoute) return false;

        if (
          currentRoute &&
          currentRoute.projectRoute &&
          this.currentRightMenuSelection?.jobNumber &&
          !(
            currentRoute.path &&
            currentRoute.path.includes(`${this.toolboxBase}/:jobNumber`)
          )
        ) {
          if (
            !(currentRoute.path ?? "").includes(
              `${this.toolboxBase}/${this.currentRightMenuSelection.jobNumber}`
            )
          )
            return false;
        }
        if (currentRoute && currentRoute.projectRoute) return true;
        if (
          currentRoute &&
          currentRoute.path &&
          currentRoute.path.includes(`${this.toolboxBase}/:`)
        )
          return true;

        // if (currentRoute && currentRoute.path.includes('/auth/')) return false;
        // if (currentRoute && currentRoute.forwardRoute) return false;
        if (this.debugMode)
          console.log(
            "Routes didnt apply to any toolbox conditions",
            currentRoute
          );
        return false;
      })
      .map((routeName: any) => {
        const currentRoute = (routes ?? {})[routeName];
        return {
          key: routeName,
          title: currentRoute?.title || routeName,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
    return currentProjectRoutes;
  }

  get rightMenuItems(): RightMenuItem[] {
    const jobStore = this.rootStore.jobStore as {
      entities: RightMenuItem[];
    };
    if (!jobStore?.entities?.length) return [];
    // console.log('jobStore.entities', jobStore.entities);
    return jobStore.entities.sort((a, b) => {
      if (a.jobNumber && b.jobNumber) {
        return Number(a.jobNumber) - Number(b.jobNumber);
      }
      return 0;
    });
  }

  get rightMenuFilteredItems() {
    // console.log('this.rightMenuItems', this.rightMenuItems);
    return this.rightMenuItems
      .slice()
      .filter((proj: RightMenuItem) => {
        if (!this.showArchiveMenuItems) {
          return !proj.archived;
        }
        return true;
      })
      .filter((proj: RightMenuItem) => {
        if (!this.rightMenuFilter) return true;
        return `${proj.jobNumber}: ${proj.name}`
          .toLowerCase()
          .includes(this.rightMenuFilter.toLowerCase());
      });
  }

  get currentRightMenuSelection() {
    const routerStore = this.rootStore.routerStore;

    // console.log(
    //   'currentRightMenuSelection this.rightMenuItems',
    //   this.rightMenuItems
    // );
    if (!this.rightMenuItems.length) return null;
    // console.log('routerStore', routerStore);
    if (!routerStore?.params || !routerStore.params.jobNumber) return null;
    // console.log('routerStore.params.jobNumber', routerStore.params.jobNumber);
    // console.log('this.rightMenuItems', this.rightMenuItems);
    return this.rightMenuItems.find(
      (job: RightMenuItem) =>
        Number(job.jobNumber) === Number(routerStore.params.jobNumber)
    );
  }

  get isRightMenuSelection() {
    const { routerStore } = this.rootStore;
    const currentPath = routerStore?.currentPath as string;
    if (!currentPath) return false;
    return currentPath.includes(`${this.toolboxBase}/`);
  }

  get omnibarSearchResults() {
    if (!this.omnibarSearchQuery || this.omnibarSearchQuery.length < 1) {
      return [];
    }

    const query = this.omnibarSearchQuery.toLowerCase();
    const results: any[] = [];
    // const { routerStore } = this.rootStore;

    // Search through left menu routes (pages) - filter for projectRoute: true
    this.leftMenuRoutes.forEach((route: any) => {
      if (route.title.toLowerCase().includes(query)) {
        // Get the actual route object to check projectRoute property
        // const routeNameWithoutSpaces = route.title.replaceAll(" ", "");
        // const routeObject = routerStore?.routes?.[routeNameWithoutSpaces];
        const routerStore = this.rootStore.routerStore as RouterStore;
        const currentRoute = routerStore.routesList.find(
          (r) =>
            r?.name?.replaceAll(" ", "") === route.title.replaceAll(" ", "")
        );
        // Only include routes that have projectRoute: true
        if (currentRoute.hidden) {
          return;
        }
        results.push({
          type: "page",
          title: route.title,
          subtitle: currentRoute?.group || "Page",
          action: () =>
            this.rootStore.routerStore?.goTo(route.title.replaceAll(" ", "")),
        });
      }
    });

    // Search through right menu items (projects/jobs)
    this.rightMenuItems.forEach((item: any) => {
      const searchText = `${item.jobNumber}: ${item.name}`.toLowerCase();
      if (searchText.includes(query)) {
        results.push({
          type: "project",
          title: `${item.jobNumber}: ${item.name}`,
          subtitle: item.address || "Project",
          action: () => {
            console.log("🎯 OmniBar navigation action called:", {
              jobNumber: item.jobNumber,
              jobNumberType: typeof item.jobNumber,
              route: this.rightNavDefaultRoute,
            });
            return this.rootStore.routerStore?.goTo(this.rightNavDefaultRoute, {
              jobNumber: item.jobNumber,
            });
          },
        });
      }
    });

    return results.slice(0, 10); // Limit to 10 results
  }

  // Enhanced OmniBar computed values - Phase 1

  // Get currently selected result
  get selectedResult() {
    if (this.showEmptyState) {
      // Flatten the tree structure for keyboard navigation
      const flattenedNavItems: any[] = [];
      this.quickNavigationItems.forEach((group) => {
        flattenedNavItems.push(...group.routes);
      });
      return flattenedNavItems[this.selectedSearchIndex] || null;
    } else {
      return this.omnibarSearchResults[this.selectedSearchIndex] || null;
    }
  }

  // Get total result count for keyboard navigation
  get totalResultCount() {
    if (this.showEmptyState) {
      // Count all items in tree structure
      return this.quickNavigationItems.reduce((total, group) => {
        return total + group.routes.length;
      }, 0);
    } else {
      return this?.omnibarSearchResults?.length || 0;
    }
  }

  // Check if showing empty state content
  get showEmptyState() {
    return !this.omnibarSearchQuery || this.omnibarSearchQuery.length === 0;
  }

  // Get categorized results for display
  get categorizedResults() {
    const results = this.showEmptyState
      ? this.omnibarFavorites
      : this.omnibarSearchResults;
    this.categorizeSearchResults(results);
    return this.searchCategories;
  }

  // Get tree navigation items grouped by module
  get quickNavigationItems() {
    const { routerStore } = this.rootStore;
    const routesList = routerStore?.routesList || [];

    // Groups hidden from the OmniBar "Page Navigation" (demo cleanup).
    // Add a route's `group` value here to drop all of its routes from the omnibar.
    const HIDDEN_OMNIBAR_GROUPS = new Set<string>([
      "Accounting",
      "Accounting Reports",
      "Estimating",
      "Project Management",
      "Operations",
      "Payroll Admin",
      "Quality Control",
      "Sales",
      "Workflows",
      "Administration",
      "Payroll",
      "Management", // auto-generated entity views (companies, BOM components, etc.) from ServiceViewRouteGenerator
    ]);

    // Individual routes hidden from the OmniBar "Page Navigation" (demo cleanup), matched by name/title.
    // Use this for one-off routes that live inside a group you want to keep otherwise.
    const HIDDEN_OMNIBAR_ROUTES = new Set<string>([
      "Department Production Schedule",
      "Projects & Clients",
    ]);

    // Group routes by their explicit group property
    const groupedRoutes = new Map<string, any[]>();

    routesList.forEach((route) => {
      // Skip routes that shouldn't appear in navigation
      if (
        route.hidden ||
        route.parentRoute ||
        route.forwardRoute ||
        route.path?.includes("/auth/") ||
        route.path?.includes("/:") || // Skip routes with parameters
        (!route.path?.startsWith("/app/") && !route.path?.startsWith("/"))
      ) {
        return;
      }

      // Use the explicit group property - skip routes without a group
      const groupKey = route.group;
      if (!groupKey) {
        return; // Skip routes that don't have a group defined
      }

      // Skip groups hidden from the omnibar (demo cleanup)
      if (HIDDEN_OMNIBAR_GROUPS.has(groupKey)) {
        return;
      }

      // Skip individual routes hidden from the omnibar (demo cleanup)
      if (
        (route.name && HIDDEN_OMNIBAR_ROUTES.has(route.name)) ||
        (route.title && HIDDEN_OMNIBAR_ROUTES.has(route.title))
      ) {
        return;
      }

      if (!groupedRoutes.has(groupKey)) {
        groupedRoutes.set(groupKey, []);
      }

      groupedRoutes.get(groupKey)!.push({
        id: route.key || route.name?.replace(/ /g, ""),
        label: route.title || route.name,
        icon: this.getIconForRoute(route),
        action: () =>
          this.rootStore.routerStore?.goTo(
            route.name?.replace(/ /g, "") || route.key
          ),
        category: groupKey.toLowerCase(),
        path: route.path,
      });
    });

    // Convert to array format for NavigationSection component
    const navigationTree: any[] = [];

    // Sort groups alphabetically, but put common ones first
    const priorityGroups = [
      "CRM",
      "Accounting",
      "Production",
      "Project Management",
      "Administration",
    ];
    const sortedGroups = Array.from(groupedRoutes.keys()).sort((a, b) => {
      const aIndex = priorityGroups.indexOf(a);
      const bIndex = priorityGroups.indexOf(b);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

    sortedGroups.forEach((groupKey) => {
      const routes = groupedRoutes.get(groupKey) || [];

      if (routes.length > 0) {
        navigationTree.push({
          groupName: groupKey,
          routes: routes.sort((a, b) => a.label.localeCompare(b.label)),
        });
      }
    });

    return navigationTree;
  }

  // Company/Plant selector visibility computed properties

  /**
   * Helper to check if a permission location matches a target location.
   * Supports wildcard matching (e.g., "/accounting/*" matches "/accounting/ap")
   */
  private matchesLocation(permissionLocation: string, targetLocation: string): boolean {
    if (!permissionLocation || !targetLocation) return false;

    // Exact match
    if (permissionLocation === targetLocation) return true;

    // Wildcard match (e.g., "/accounting/*" matches "/accounting/ap")
    if (permissionLocation.endsWith("/*")) {
      const basePath = permissionLocation.slice(0, -2);
      return targetLocation.startsWith(basePath);
    }

    // Parent path match (e.g., "/accounting" allows "/accounting/ap")
    if (targetLocation.startsWith(permissionLocation + "/")) return true;

    return false;
  }

  /**
   * Get the current user's permissions from AuthStore.
   */
  private get userPermissions(): UserPermissionWithPlant[] {
    const authStore = this.rootStore.authStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((authStore as any)?.userPermissions || []) as UserPermissionWithPlant[];
  }

  /**
   * Get user's default plant ID from their profile.
   * Falls back to first accessible plant if not set.
   */
  get userDefaultPlantId(): string | null {
    const authStore = this.rootStore.authStore;
     
    const user = authStore?.currentUser as { defaultPlantId?: string } | undefined;

    // Use user's configured default plant
    if (user?.defaultPlantId) {
      return user.defaultPlantId;
    }

    // Fallback to first accessible plant for current route
    const accessiblePlants = this.accessiblePlantsForCurrentRoute;
    if (accessiblePlants.length > 0 && accessiblePlants[0] !== "all") {
      return accessiblePlants[0];
    }

    return null;
  }

  /**
   * Get plants the user can access for the current route.
   * Returns array of plant IDs, or includes "all" if user has all-plants access.
   */
  get accessiblePlantsForCurrentRoute(): string[] {
    const authStore = this.rootStore.authStore;
    const routerStore = this.rootStore.routerStore;
    const currentRoute = routerStore?.currentRoute;

    // Admins have access to all plants
    if (authStore?.isAdminUser) {
      return ["all"];
    }

    // Get permission path from route config, fallback to route path
    const permissionPath = currentRoute?.permissionPath || currentRoute?.path || "";

    // Filter permissions that match this route's location
    const matchingPerms = this.userPermissions.filter((p) =>
      this.matchesLocation(p.location, permissionPath) && p.findAllowed
    );

    // If no matching permissions, return empty array
    if (matchingPerms.length === 0) {
      return [];
    }

    // Check if any permission grants "all" plants access
    const hasAllAccess = matchingPerms.some((p) => p.plantId === "all");
    if (hasAllAccess) {
      return ["all"];
    }

    // Collect unique plant IDs from permissions
    const plantIds = new Set<string>();
    matchingPerms.forEach((p) => {
      if (p.plantId && p.plantId !== "all") {
        plantIds.add(p.plantId);
      }
    });

    return Array.from(plantIds);
  }

  /**
   * Whether the user can select "All Plants" option for the current route.
   * Only true if user has a permission with plantId: "all" for this route.
   */
  get canSelectAllPlants(): boolean {
    const authStore = this.rootStore.authStore;

    // Admins can always select all plants
    if (authStore?.isAdminUser) return true;

    const accessiblePlants = this.accessiblePlantsForCurrentRoute;
    return accessiblePlants.includes("all");
  }

  /**
   * Check if user has access to multiple plants for the current route.
   */
  get userHasMultiplePlantsForRoute(): boolean {
    const accessiblePlants = this.accessiblePlantsForCurrentRoute;

    // "all" means access to all plants
    if (accessiblePlants.includes("all")) return true;

    // More than one specific plant
    return accessiblePlants.length > 1;
  }

  /**
   * Whether the company selector should be visible for the current route.
   * Checks route configuration and user's plant access.
   */
  get shouldShowCompanySelector(): boolean {
    const routerStore = this.rootStore.routerStore;
    const currentRoute = routerStore?.currentRoute;

    // Check route config - only show if route explicitly enables it
    if (!currentRoute?.showCompanySelector) return false;

    // Check user has multiple plants for this route
    if (!this.userHasMultiplePlantsForRoute) return false;

    return true;
  }

  /**
   * Initialize selected company to user's default when entering a route.
   * Should be called from route beforeEnter hooks.
   */
  initializeSelectedPlant = () => {
    // Only initialize if no plant is currently selected
    if (this.selectedCompanyId === null) {
      const defaultPlant = this.userDefaultPlantId;
      if (defaultPlant) {
        this.setSelectedCompanyId(defaultPlant);
      }
    }
  };

  // Helper method to get icons for different route types
  private getIconForRoute(route: RouteType): string {
    const path = route.path?.toLowerCase() || "";
    const name = route.name?.toLowerCase() || "";

    if (name.includes("dashboard")) return "�";
    if (path.includes("/crm/") || name.includes("crm")) return "👥";
    if (path.includes("/accounting/") || name.includes("accounting"))
      return "💰";
    if (path.includes("/production/") || name.includes("production"))
      return "🏭";
    if (path.includes("/project") || name.includes("project")) return "📁";
    if (path.includes("/purchase") || name.includes("purchase")) return "🛒";
    if (path.includes("/inventory") || name.includes("inventory")) return "📦";
    if (path.includes("/quality") || name.includes("quality")) return "✅";
    if (path.includes("/admin") || name.includes("admin")) return "⚙️";
    if (path.includes("/settings") || name.includes("settings")) return "🔧";
    if (path.includes("/schedule") || name.includes("schedule")) return "📅";
    if (path.includes("/dispatch") || name.includes("dispatch")) return "🚛";
    if (path.includes("/yard") || name.includes("yard")) return "🏗️";

    return "📄"; // Default icon
  }
}

export default AppLayoutStore;
